import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Info, X, Zap } from 'lucide-react'
import { useAppStore } from '@/shared/stores/appStore'
import type { Toast } from '@/shared/stores/appStore'
import styles from './ToastHost.module.css'

// Mounted once, at the root. Every addToast() in the app pushed onto a queue nothing
// rendered, so the whole toast vocabulary — including the admin panel's "Erro ao
// finalizar" — was written but never shown, and a refused action looked like a dead button.

const DEFAULT_DURATION_MS = 5_000
// Errors carry the reason the server refused; give them time to be read, and never let one
// vanish while it is the only explanation of what just happened.
const ERROR_DURATION_MS   = 12_000

const ICONS = {
  success: CheckCircle2,
  error:   AlertTriangle,
  info:    Info,
  goal:    Zap,
} as const

export function ToastHost() {
  const toasts = useAppStore(s => s.toasts)

  if (toasts.length === 0) return null

  return createPortal(
    <div className={styles.stack}>
      {toasts.map(toast => <ToastRow key={toast.id} toast={toast} />)}
    </div>,
    document.body,
  )
}

function ToastRow({ toast }: { toast: Toast }) {
  const removeToast = useAppStore(s => s.removeToast)
  const isError     = toast.type === 'error'

  useEffect(() => {
    const ms = toast.durationMs
      ?? (isError ? ERROR_DURATION_MS : DEFAULT_DURATION_MS)
    const timer = setTimeout(() => removeToast(toast.id), ms)
    return () => clearTimeout(timer)
  }, [toast.id, toast.durationMs, isError, removeToast])

  const Icon = ICONS[toast.type]

  return (
    <div
      className={`${styles.toast} ${styles[toast.type]}`}
      // An error is the outcome of something the user just did and must not wait behind
      // whatever else the page is announcing.
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <Icon size={16} className={styles.icon} aria-hidden="true" />
      <div className={styles.content}>
        <p className={styles.title}>{toast.title}</p>
        {toast.body && <p className={styles.body}>{toast.body}</p>}
      </div>
      <button
        type="button"
        className={styles.dismiss}
        onClick={() => removeToast(toast.id)}
        aria-label="Fechar aviso"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  )
}
